import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, CheckCircle, Target, User, TrendingUp, Calendar } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useUser } from "@/contexts/UserContext";
import { wellnessService } from "@/services/wellnessService";
import { calendarService } from "@/services/calendarService";
import { goalsService } from "@/services/goalsService";

interface NextAction {
  title: string;
  description: string;
  action: () => void;
  priority: "high" | "medium" | "low";
  icon: React.ReactNode;
  category: string;
}

export default function NextStepsCard() {
  const navigate = useNavigate();
  const { user } = useUser();
  const [checkInCount, setCheckInCount] = useState(0);
  const [profileComplete, setProfileComplete] = useState(false);
  const [hasAvatar, setHasAvatar] = useState(false);
  const [hasCalendar, setHasCalendar] = useState(false);
  const [hasGoals, setHasGoals] = useState(false);
  const [hasTodayCheckIn, setHasTodayCheckIn] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadUserProgress = async () => {
      if (!user?.id) return;

      try {
        // Parallel fetch all data - services return data directly, not { success, data }
        const [statsData, todayCheckInData, calendarData, goalsData] = await Promise.all([
          // Get check-ins count from stats
          wellnessService.getStats().catch(() => null),
          // Check if today's check-in exists
          wellnessService.getTodaysCheckIn().catch(() => null),
          // Get calendar integrations
          calendarService.getIntegrations().catch(() => []),
          // Get goals
          goalsService.getGoals().catch(() => []),
        ]);

        // Set check-in count
        if (statsData && typeof statsData.totalCheckIns === 'number') {
          setCheckInCount(statsData.totalCheckIns);
        }

        // Check if today's check-in exists
        setHasTodayCheckIn(!!todayCheckInData);

        // Check profile completion (avatar and calendar)
        const hasAvatarData = !!(user?.avatarUrl);
        setHasAvatar(hasAvatarData);
        
        // Check calendar integration
        const calendarIntegrations = Array.isArray(calendarData) ? calendarData : [];
        const hasCalendarData = calendarIntegrations.length > 0;
        setHasCalendar(hasCalendarData);
        
        // Profile is complete if user has calendar connection (avatar is always available via generation)
        setProfileComplete(hasCalendarData);

        // Check if user has goals
        const goalsArray = Array.isArray(goalsData) ? goalsData : (goalsData?.goals || []);
        setHasGoals(goalsArray.length > 0);

        if (process.env.NODE_ENV === 'development') {
          console.log('NextStepsCard - Status check:', {
            checkInCount: statsData?.totalCheckIns || 0,
            hasTodayCheckIn: !!todayCheckInData,
            hasAvatar: hasAvatarData,
            hasCalendar: hasCalendarData,
            profileComplete: hasCalendarData,
            hasGoals: goalsArray.length > 0
          });
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error("Error loading user progress:", error);
        }
      } finally {
        setLoading(false);
      }
    };

    loadUserProgress();
  }, [user?.id, user?.avatarUrl]);

  const getNextAction = (): NextAction | null => {
    // First check-in is highest priority
    if (checkInCount === 0) {
      return {
        title: "Complete Your First Check-in",
        description: "Track your energy and set goals for today",
        action: () => navigate('/check-in'),
        priority: "high",
        icon: <CheckCircle className="h-5 w-5" />,
        category: "Getting Started"
      };
    }
    
    // Profile completion is next priority (only requires calendar now)
    if (!profileComplete) {
      return {
        title: "Complete Your Profile", 
        description: `Connect calendar to personalize your experience`,
        action: () => {
          // Trigger the profile settings widget directly
          const settingsButton = document.querySelector('[data-testid="profile-settings-trigger"]') as HTMLButtonElement;
          if (settingsButton) {
            settingsButton.click();
            // Open the calendar/integrations tab after the modal opens
            setTimeout(() => {
              const calendarTab = document.querySelector('[data-value="integrations"]') as HTMLButtonElement;
              if (calendarTab) {
                calendarTab.click();
              }
            }, 300);
          } else {
            // Fallback to settings page if modal not available
            navigate('/settings');
          }
        },
        priority: "medium",
        icon: <User className="h-5 w-5" />,
        category: "Setup"
      };
    }
    
    // Goals after they have initial check-in data
    if (!hasGoals && checkInCount >= 1) {
      return {
        title: "Set Your First Goal",
        description: "Define what you want to achieve this week",
        action: () => navigate('/profile', { state: { activeTab: 'goals' } }),
        priority: "medium",
        icon: <Target className="h-5 w-5" />,
        category: "Growth"
      };
    }
    
    // Building consistency after profile and goals are set
    if (checkInCount < 7 && profileComplete && hasGoals) {
      return {
        title: "Build Your Check-in Streak",
        description: `${7 - checkInCount} more check-ins to unlock weekly insights`,
        action: () => navigate('/check-in'),
        priority: "low",
        icon: <TrendingUp className="h-5 w-5" />,
        category: "Progress"
      };
    }

    // Remove this - moved up to happen after first check-in

    // Weekly reflection after a week
    if (checkInCount >= 7 && checkInCount % 7 === 0) {
      return {
        title: "Time for Weekly Reflection",
        description: "Review your progress and set intentions for next week",
        action: () => navigate('/patterns'),
        priority: "low",
        icon: <Calendar className="h-5 w-5" />,
        category: "Reflection"
      };
    }

    // Show daily check-in only if not done today
    if (!hasTodayCheckIn) {
      return {
        title: "Complete Today's Check-in",
        description: "Keep your streak going and track today's progress",
        action: () => navigate('/check-in'),
        priority: "low",
        icon: <CheckCircle className="h-5 w-5" />,
        category: "Daily"
      };
    }

    // If everything is complete, return null (no card shown)
    return null;
  };

  if (loading) {
    return null;
  }

  const nextAction = getNextAction();
  if (!nextAction) return null;

  const priorityColors = {
    high: "border-primary bg-primary/5",
    medium: "border-blue-200 bg-blue-50 dark:bg-blue-950/20",
    low: "border-border"
  };

  const priorityBadgeVariants = {
    high: "default" as const,
    medium: "secondary" as const,
    low: "outline" as const
  };

  return (
    <Card className={`${priorityColors[nextAction.priority]} transition-all hover:shadow-md`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${
              nextAction.priority === "high" 
                ? "bg-primary/10 text-primary" 
                : "bg-muted text-muted-foreground"
            }`}>
              {nextAction.icon}
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <h4 className="font-medium">{nextAction.title}</h4>
                <Badge 
                  variant={priorityBadgeVariants[nextAction.priority]} 
                  className="text-xs"
                >
                  {nextAction.category}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">{nextAction.description}</p>
            </div>
          </div>
          <Button 
            onClick={nextAction.action} 
            variant={nextAction.priority === "high" ? "default" : "outline"}
            size="sm"
            className="shrink-0"
          >
            <span className="hidden sm:inline mr-1">Next Step</span>
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}